import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { User, UserDetails } from "api/controlPlaneApi";
import {
  addUser,
  deleteUser,
  editUser,
  fetchUserDetails,
  fetchUsers,
} from "./usersThunks";

export interface UsersState {
  loadingUsers: boolean;
  loadingUserDetails: boolean;
  loadingDeleteUser: boolean;
  loadingAddUser: boolean;
  loadingEditUser: boolean;
  users: User[];
  userDetails: UserDetails | null;
}

const initialState: UsersState = {
  loadingUsers: false,
  loadingUserDetails: false,
  loadingDeleteUser: false,
  loadingAddUser: false,
  loadingEditUser: false,
  users: [],
  userDetails: null,
};

export const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setLoadingUsers: (state, action: PayloadAction<boolean>) => {
      state.loadingUsers = action.payload;
    },
    setLoadingUserDetails: (state, action: PayloadAction<boolean>) => {
      state.loadingUserDetails = action.payload;
    },
    setLoadingDeleteUser: (state, action: PayloadAction<boolean>) => {
      state.loadingDeleteUser = action.payload;
    },
    setLoadingAddUser: (state, action: PayloadAction<boolean>) => {
      state.loadingAddUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loadingUsers = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.pending, (state) => {
        state.loadingUsers = true;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loadingUsers = false;
        state.users = [];
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loadingUserDetails = false;
        state.userDetails = action.payload;
      })
      .addCase(fetchUserDetails.pending, (state) => {
        state.loadingUsers = true;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loadingUserDetails = false;
        state.users = [];
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loadingDeleteUser = false;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loadingDeleteUser = true;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loadingDeleteUser = false;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loadingAddUser = false;
      })
      .addCase(addUser.pending, (state) => {
        state.loadingAddUser = true;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loadingAddUser = false;
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.loadingEditUser = false;
      })
      .addCase(editUser.pending, (state) => {
        state.loadingEditUser = true;
      })
      .addCase(editUser.rejected, (state, action) => {
        state.loadingEditUser = false;
      });
  },
});

// Reducer actions
export const {
  setLoadingUsers,
  setLoadingUserDetails,
  setLoadingDeleteUser,
  setLoadingAddUser,
} = userSlice.actions;

// Reducer selectors

export const users = (state: RootState) => state.users.users;
export const userDetails = (state: RootState) => state.users.userDetails;
export const loadingUsers = (state: RootState) => state.users.loadingUsers;
export const loadingUserDetails = (state: RootState) =>
  state.users.loadingUserDetails;
export const loadingDeleteUser = (state: RootState) =>
  state.users.loadingDeleteUser;
export const loadingAddUser = (state: RootState) => state.users.loadingAddUser;
export const loadingEditUser = (state: RootState) =>
  state.users.loadingEditUser;

export default userSlice.reducer;
